/**
 * แผงแสดงคุณภาพพิกัด
 *
 * หน้าที่เดียว: ทำให้ผู้ใช้เห็นภายในหนึ่งวินาทีว่า "ใช้ค่านี้ได้หรือยัง"
 * วงแหวนบอกความคืบหน้า ตัวเลขใหญ่บอกค่าจริง แถบข้อความบอกว่าต้องทำอะไรต่อ
 */
import type { GpsSettings } from '../../shared/config';
import { accuracyProgress, gpsQuality, GPS_QUALITY_LABELS_TH } from '../../shared/gps';
import { formatAgo } from '../../shared/time';
import { coordinate, metres } from '../lib/format';
import type { GpsPhase, GpsReading } from '../lib/gps';

interface Props {
  phase: GpsPhase;
  reading: GpsReading | null;
  settings: GpsSettings;
  secondsLeft: number;
  samples: number;
  errorMessage: string | null;
  ageSeconds: number;
  fresh: boolean;
}

const RING_COLOR: Record<string, string> = {
  excellent: 'var(--ok)',
  good: 'var(--ok)',
  fair: 'var(--warn)',
  poor: 'var(--bad)',
};

export function GpsPanel({
  phase,
  reading,
  settings,
  secondsLeft,
  samples,
  errorMessage,
  ageSeconds,
  fresh,
}: Props) {
  const quality = reading ? gpsQuality(reading.accuracyM, settings) : 'poor';
  const progress = reading ? accuracyProgress(reading.accuracyM, settings.thresholdM) : 0;
  const searching = phase === 'searching' || phase === 'requesting';

  return (
    <div className="gps-readout">
      <div
        className="gps-ring"
        style={
          {
            '--ring-fill': `${Math.round(progress * 100)}%`,
            '--ring-color': RING_COLOR[quality],
          } as React.CSSProperties
        }
        role="img"
        aria-label={
          reading
            ? `ความแม่นยำ ${metres(reading.accuracyM)} คุณภาพ${GPS_QUALITY_LABELS_TH[quality]}`
            : 'กำลังค้นหาสัญญาณ'
        }
      >
        {searching && <span className="gps-sweep" aria-hidden="true" />}
        <span className="gps-ring-value">
          <b>{reading ? Math.round(reading.accuracyM) : '--'}</b>
          <span>เมตร</span>
        </span>
      </div>

      <p style={{ marginTop: 'var(--space-3)' }}>
        <StatusLine
          phase={phase}
          quality={quality}
          settings={settings}
          secondsLeft={secondsLeft}
          fresh={fresh}
          hasReading={reading !== null}
        />
      </p>

      {errorMessage && (
        <div className="banner banner-warn" style={{ marginTop: 'var(--space-3)' }} role="alert">
          <span>{errorMessage}</span>
        </div>
      )}

      {reading && (
        <dl className="kv" style={{ marginTop: 'var(--space-4)' }}>
          <div>
            <dt>ละติจูด</dt>
            <dd>{coordinate(reading.latitude)}</dd>
          </div>
          <div>
            <dt>ลองจิจูด</dt>
            <dd>{coordinate(reading.longitude)}</dd>
          </div>
          <div>
            <dt>ความแม่นยำ</dt>
            <dd>{metres(reading.accuracyM)}</dd>
          </div>
          <div>
            <dt>เกณฑ์ที่ตั้งไว้</dt>
            <dd>{metres(settings.thresholdM)}</dd>
          </div>
          <div>
            <dt>อายุพิกัด</dt>
            <dd style={{ color: fresh ? undefined : 'var(--bad)' }}>
              {formatAgo(reading.positionTimestamp)}
            </dd>
          </div>
          {samples > 1 && (
            <div>
              <dt>จำนวนครั้งที่วัด</dt>
              <dd>{samples}</dd>
            </div>
          )}
        </dl>
      )}

      {!fresh && reading && (
        <div className="banner banner-warn" style={{ marginTop: 'var(--space-3)' }} role="alert">
          <span>
            พิกัดนี้เก่า {ageSeconds} วินาทีแล้ว เกินเกณฑ์ {settings.maxPositionAgeSeconds} วินาที
            กรุณาวัดใหม่ก่อนบันทึก
          </span>
        </div>
      )}
    </div>
  );
}

function StatusLine({
  phase,
  quality,
  settings,
  secondsLeft,
  fresh,
  hasReading,
}: {
  phase: GpsPhase;
  quality: string;
  settings: GpsSettings;
  secondsLeft: number;
  fresh: boolean;
  hasReading: boolean;
}) {
  if (phase === 'idle') {
    return <span className="muted">กดปุ่มด้านล่างเพื่อเริ่มหาตำแหน่ง</span>;
  }
  if (phase === 'requesting') {
    return <strong>กำลังขอสิทธิ์เข้าถึงตำแหน่ง…</strong>;
  }
  if (phase === 'searching') {
    return (
      <strong>
        กำลังหาสัญญาณ… เหลือเวลา {secondsLeft} วินาที
        {hasReading && ` (ตอนนี้คุณภาพ${GPS_QUALITY_LABELS_TH[quality as 'poor']})`}
      </strong>
    );
  }
  if (phase === 'locked') {
    if (!fresh) return <strong style={{ color: 'var(--bad)' }}>พิกัดเก่าเกินไป ต้องวัดใหม่</strong>;
    return (
      <strong style={{ color: 'var(--ok)' }}>
        ผ่านเกณฑ์ · คุณภาพ{GPS_QUALITY_LABELS_TH[quality as 'good']} ·
        หยุดวัดแล้วเพื่อประหยัดแบตเตอรี่
      </strong>
    );
  }
  if (phase === 'timeout') {
    return (
      <strong style={{ color: 'var(--warn)' }}>ยังไม่ผ่านเกณฑ์ {settings.thresholdM} เมตร</strong>
    );
  }
  return <strong style={{ color: 'var(--bad)' }}>หาตำแหน่งไม่สำเร็จ</strong>;
}
